/**
 * CalendarLayout Component
 *
 * Main calendar view for visualizing and planning posts
 */

import { useState, useCallback, useMemo } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import enUS from "date-fns/locale/en-US";
import Grid from "@mui/material/Grid";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAlert from "components/MDAlert";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Calendar components
import CalendarToolbar from "components/Calendar/CalendarToolbar";
import CalendarStats from "components/Calendar/CalendarStats";
import CalendarEvent from "components/Calendar/CalendarEvent";
import PostDetailsModal from "components/Calendar/PostDetailsModal";
import UpcomingPosts from "components/Calendar/UpcomingPosts";

// Hooks
import useCalendarPosts from "hooks/useCalendarPosts";

// Calendar styles
import "react-big-calendar/lib/css/react-big-calendar.css";

// Configure date-fns localizer
const locales = {
  "en-US": enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

function CalendarLayout() {
  const [currentView, setCurrentView] = useState("month");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedPost, setSelectedPost] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch posts with status filter
  const filters = useMemo(
    () => ({
      status: statusFilter,
    }),
    [statusFilter]
  );

  const { posts, loading, error } = useCalendarPosts(filters);

  // Transform posts to calendar events
  const events = useMemo(() => {
    return posts.map((post) => {
      const date = new Date(post.date || post.created_at);
      return {
        id: post.id,
        title: post.title || "Untitled Post",
        start: date,
        end: date,
        allDay: true,
        resource: post,
        status: post.status,
      };
    });
  }, [posts]);

  // Event handlers
  const handleSelectEvent = useCallback((event) => {
    setSelectedPost(event.resource);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPost(null);
  };

  const handleUpcomingPostClick = (post) => {
    setSelectedPost(post);
    setIsModalOpen(true);
  };

  const handleNavigate = useCallback((newDate) => {
    setCurrentDate(newDate);
  }, []);

  const handleViewChange = useCallback((newView) => {
    setCurrentView(newView);
  }, []);

  // Custom event style getter
  const eventStyleGetter = useCallback((event) => {
    let backgroundColor = "#1A73E8"; // Default blue
    let borderColor = "#1A73E8";

    switch (event.status) {
      case "Posted":
        backgroundColor = "#4CAF50"; // Green
        borderColor = "#4CAF50";
        break;
      case "Pending Approval":
      case "Good to go":
        backgroundColor = "#FB8C00"; // Orange
        borderColor = "#FB8C00";
        break;
      case "Rejected":
        backgroundColor = "#F44335"; // Red
        borderColor = "#F44335";
        break;
      default:
        backgroundColor = "#1A73E8"; // Blue
        borderColor = "#1A73E8";
    }

    return {
      style: {
        backgroundColor,
        borderColor,
        borderRadius: "4px",
        opacity: 0.9,
        color: "white",
        border: "none",
        display: "block",
        fontSize: "0.75rem",
        padding: "2px 4px",
      },
    };
  }, []);

  // Custom component for events
  const EventComponent = useCallback(({ event }) => {
    return <CalendarEvent event={event} />;
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <MDTypography variant="h3" fontWeight="bold">
            Content Calendar
          </MDTypography>
          <MDTypography variant="body2" color="text">
            View your content publishing schedule and plan future posts
          </MDTypography>
        </MDBox>

        {/* Error Alert */}
        {error && (
          <MDBox mb={3}>
            <MDAlert color="error" dismissible>
              <MDTypography variant="body2" color="white">
                {error}
              </MDTypography>
            </MDAlert>
          </MDBox>
        )}

        {/* Calendar Statistics */}
        {!loading && <CalendarStats posts={posts} />}

        {/* Calendar and Sidebar Grid */}
        <Grid container spacing={3}>
          {/* Main Calendar View */}
          <Grid item xs={12} lg={9}>
            <MDBox
              sx={{
                backgroundColor: "white",
                borderRadius: "16px",
                boxShadow: ({ boxShadows }) => boxShadows.lg,
                p: { xs: 2, sm: 2.5, md: 3 },
                minHeight: { xs: "500px", sm: "550px", md: "600px" },
                overflow: "auto",
                WebkitOverflowScrolling: "touch",
                "& .rbc-calendar": {
                  fontFamily: "Roboto, sans-serif",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                },
                "& .rbc-header": {
                  padding: { xs: "8px 0", sm: "10px 0", md: "12px 0" },
                  fontWeight: 600,
                  fontSize: { xs: "0.75rem", sm: "0.8125rem", md: "0.875rem" },
                  color: ({ palette }) => palette.grey[700],
                  borderBottom: ({ borders }) =>
                    borders.borderWidth[1] + " solid " + borders.borderColor,
                },
                "& .rbc-today": {
                  backgroundColor: ({ palette }) => palette.info.main + "10",
                },
                "& .rbc-off-range-bg": {
                  backgroundColor: ({ palette }) => palette.grey[100],
                },
                "& .rbc-event": {
                  padding: { xs: "3px 4px", sm: "4px 6px" },
                  borderRadius: "6px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    transform: "scale(1.05)",
                    boxShadow: ({ boxShadows }) => boxShadows.sm,
                  },
                },
                "& .rbc-event-label": {
                  fontSize: { xs: "0.65rem", sm: "0.7rem" },
                },
                "& .rbc-toolbar": {
                  display: "none", // Hide default toolbar since we have custom one
                },
                "& .rbc-month-view, & .rbc-time-view, & .rbc-agenda-view": {
                  border: "none",
                },
                "& .rbc-day-slot .rbc-time-slot": {
                  borderTop: ({ borders }) =>
                    borders.borderWidth[1] + " solid " + borders.borderColor,
                },
                "& .rbc-date-cell": {
                  padding: { xs: "4px", sm: "6px", md: "8px" },
                },
                "& .rbc-button-link": {
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  fontWeight: 500,
                },
              }}
            >
              {/* Custom Toolbar */}
              <CalendarToolbar
                date={currentDate}
                view={currentView}
                views={["month", "week", "day", "agenda"]}
                label={format(currentDate, "MMMM yyyy")}
                onNavigate={(action) => {
                  if (action === "TODAY") {
                    handleNavigate(new Date());
                  } else if (action === "PREV") {
                    const newDate = new Date(currentDate);
                    if (currentView === "month") {
                      newDate.setMonth(newDate.getMonth() - 1);
                    } else if (currentView === "week") {
                      newDate.setDate(newDate.getDate() - 7);
                    } else {
                      newDate.setDate(newDate.getDate() - 1);
                    }
                    handleNavigate(newDate);
                  } else if (action === "NEXT") {
                    const newDate = new Date(currentDate);
                    if (currentView === "month") {
                      newDate.setMonth(newDate.getMonth() + 1);
                    } else if (currentView === "week") {
                      newDate.setDate(newDate.getDate() + 7);
                    } else {
                      newDate.setDate(newDate.getDate() + 1);
                    }
                    handleNavigate(newDate);
                  }
                }}
                onView={handleViewChange}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
              />

              {/* Calendar */}
              {loading ? (
                <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="500px">
                  <MDTypography variant="h6" color="text">
                    Loading calendar...
                  </MDTypography>
                </MDBox>
              ) : (
                <Calendar
                  localizer={localizer}
                  events={events}
                  startAccessor="start"
                  endAccessor="end"
                  style={{ height: 600 }}
                  view={currentView}
                  onView={handleViewChange}
                  date={currentDate}
                  onNavigate={handleNavigate}
                  onSelectEvent={handleSelectEvent}
                  eventPropGetter={eventStyleGetter}
                  components={{
                    event: EventComponent,
                  }}
                  popup
                  views={["month", "week", "day", "agenda"]}
                />
              )}
            </MDBox>
          </Grid>

          {/* Upcoming Posts Sidebar */}
          <Grid item xs={12} lg={3}>
            <UpcomingPosts posts={posts} onPostClick={handleUpcomingPostClick} />
          </Grid>
        </Grid>

        {/* Post Details Modal */}
        <PostDetailsModal open={isModalOpen} onClose={handleCloseModal} post={selectedPost} />
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default CalendarLayout;
